import {CATEGORIES_MAPPING, GROUPS, STATUS} from "../../../utils/constants";
import {timestampToDateTimeString} from "../model/dashboardHelpers";
import {ICase} from "../../../types/Case";

import * as XLSX from "xlsx";
import {
  calculateSatisfaction,
  calculateTimeElapsed,
  countByAge,
  countByCategory,
  countByGender,
  countByRegion,
} from "../model/dashboardAnalytics";
import {saveWorkbook} from "../../../utils/excel";

const CASES_DETAIL_EXCEL_HEADER = [
  '派案機構', '派案時間', '申請人姓名', '服務業務組別', '職稱', '聯繫電話', '個案姓名', '個案性別', '個案身分證',
  '個案生日', '個案住址', '是否有管路', '主要照顧者姓名', '與個案關係', '個案/家屬電話', '目前接受在宅照服項目',
  '個案現況描述', '申請時間', '接案時間'
];

export const useExportDashboard = ({
  organizationMapping,
  organizationCount,
  userCount,
  cases,
}) => {
  const getAssignedOrganizationName = (c: ICase) => {
    if (c.acceptedByOrganization) return organizationMapping[c.acceptedByOrganization];
    if (c.assignedToOrganization) return organizationMapping[c.assignedToOrganization];
    return ''
  }

  // export cases detail to excel file
  const exportCasesDetail = () => {
    const values = []

    const workbook = XLSX.utils.book_new();
    for (const caseStatus of STATUS) {
      values.splice(0, values.length);
      values.push(CASES_DETAIL_EXCEL_HEADER);
      const filteredCases = cases
        .filter(c => c.status === caseStatus.name)
        .sort((a, b) => {
          const dateTimeStringA = timestampToDateTimeString(a.createAt)
          const dateTimeStringB = timestampToDateTimeString(b.createAt)
          return dateTimeStringB.localeCompare(dateTimeStringA)
        });

      for (const c of filteredCases) {
        const row = [
          getAssignedOrganizationName(c),
          timestampToDateTimeString(c.assignedAt),
          c.applicantName,
          CATEGORIES_MAPPING[c.category],
          c.applicantTitle,
          c.applicantPhone,
          c.patientName,
          c.patientGender,
          c.patientId,
          c.patientBirthday,
          c.patientAddress,
          c.patientHasTube,
          c.primaryCaregiverName,
          c.relationshipWithPatient,
          c.contactNumber,
          c.currentHomeCareService,
          c.description,
          timestampToDateTimeString(c.createAt),
          timestampToDateTimeString(c.acceptedAt),
        ]
        values.push(row)
      }
      const worksheet = XLSX.utils.aoa_to_sheet(values);
      XLSX.utils.book_append_sheet(workbook, worksheet, caseStatus.label);
    }

    saveWorkbook({ workbook, fileName: '貼心到宅案件明細'})
  }

  // export cases statistic to excel file
  const exportCaseStatistics = () => {
    const extendedGroups = [{ path: '', shortName: ''}, ...GROUPS]
    // =====================================================
    // ==================== worksheet 1 ====================
    // =====================================================
    const worksheet1Values = [
      ["業務組別總數", "使用者總數", "總案件數"],
      [organizationCount, userCount, cases.length]
    ]
    const worksheet1 = XLSX.utils.aoa_to_sheet(worksheet1Values);

    // =====================================================
    // ==================== worksheet 2 ====================
    // =====================================================
    const worksheet2Values = []
    for (const group of extendedGroups) {
      const filteredCases = cases.filter(c => {
        if (group.path === '') return true
        return (c.category === group.path)
      })
      const { tempAverageScoreArray, tempOverallAverage } = calculateSatisfaction(filteredCases)

      worksheet2Values.push([
        `${group.shortName}整體滿意度總平均`,
        "本次服務符",
        "服務適配度",
        "工作人員態度",
        "對於服務過程有疑慮可聯繫"
      ])
      worksheet2Values.push([
        tempOverallAverage.toFixed(2),
        ...tempAverageScoreArray.map(t => t.count.toFixed(2))
      ])
      worksheet2Values.push([])
    }
    const worksheet2 = XLSX.utils.aoa_to_sheet(worksheet2Values);

    // =====================================================
    // ==================== worksheet 3 ====================
    // =====================================================
    const worksheet3Values = []

    for (const group of extendedGroups) {
      const filteredCases = cases.filter(c => {
        if (group.path === '') return true
        return (c.category === group.path)
      })

      const {tempAverageAcceptedDays, tempAverageContactDays, tempAverageServiceDays} = calculateTimeElapsed(filteredCases)
      worksheet3Values.push([
        `${group.path === '' ? '整體' : group.shortName}`,
        "案件收案平均天數",
        "案件聯繫平均天數",
        "案件服務平均天數",
      ])
      worksheet3Values.push([
        tempAverageAcceptedDays.toFixed(2),
        tempAverageContactDays.toFixed(2),
        tempAverageServiceDays.toFixed(2)
      ])
      worksheet3Values.push([])
    }
    const worksheet3 = XLSX.utils.aoa_to_sheet(worksheet3Values);

    // =====================================================
    // ==================== worksheet 4 ====================
    // =====================================================
    const tempCountByCategory = countByCategory(cases)
    const worksheet4Values = []
    worksheet4Values.push(['類別', '案件數'])

    for (const row of tempCountByCategory) {
      worksheet4Values.push([row.category, row.count])
    }
    const worksheet4 = XLSX.utils.aoa_to_sheet(worksheet4Values);

    // =====================================================
    // ==================== worksheet 5 ====================
    // =====================================================
    const worksheet5Values = []
    extendedGroups.forEach((group, index) => {
      const filteredCases = cases.filter(c => {
        if (group.path === '') return true
        return (c.category === group.path)
      })
      const colOffset = index * 3;
      worksheet5Values[0] = worksheet5Values[0] || [];
      worksheet5Values[0][colOffset] = "類別"
      worksheet5Values[0][colOffset + 1] = "性別"
      worksheet5Values[0][colOffset + 2] = "人數"

      const tempCountByGender = countByGender(filteredCases)
      tempCountByGender.forEach((rowData, rowIndex) => {
        const row = rowIndex + 1;
        worksheet5Values[row] = worksheet5Values[row] || [];
        worksheet5Values[row][colOffset] = `${row === 1 ? (group.path === '' ? '整體' : group.shortName) : ''}`;
        worksheet5Values[row][colOffset + 1] = rowData.gender;
        worksheet5Values[row][colOffset + 2] = rowData.count || 0;
      })
    })
    const worksheet5 = XLSX.utils.aoa_to_sheet(worksheet5Values);

    // =====================================================
    // ==================== worksheet 6 ====================
    // =====================================================
    const worksheet6Values = []
    extendedGroups.forEach((group, index) => {
      const filteredCases = cases.filter(c => {
        if (group.path === '') return true
        return (c.category === group.path)
      })
      const colOffset = index * 3;
      worksheet6Values[0] = worksheet6Values[0] || [];
      worksheet6Values[0][colOffset] = "類別"
      worksheet6Values[0][colOffset + 1] = "年齡區間"
      worksheet6Values[0][colOffset + 2] = "人數"

      const tempCountByGender = countByAge(filteredCases)
      tempCountByGender.forEach((rowData, rowIndex) => {
        const row = rowIndex + 1;
        worksheet6Values[row] = worksheet6Values[row] || [];
        worksheet6Values[row][colOffset] = `${row === 1 ? (group.path === '' ? '整體' : group.shortName) : ''}`;
        worksheet6Values[row][colOffset + 1] = rowData.label;
        worksheet6Values[row][colOffset + 2] = rowData.count || 0;
      })
    })
    const worksheet6 = XLSX.utils.aoa_to_sheet(worksheet6Values);

    // =====================================================
    // ==================== worksheet 7 ====================
    // =====================================================
    const worksheet7Values = []
    extendedGroups.forEach((group, index) => {
      const filteredCases = cases.filter(c => {
        if (group.path === '') return true
        return (c.category === group.path)
      })
      const colOffset = index * 3;
      worksheet7Values[0] = worksheet7Values[0] || [];
      worksheet7Values[0][colOffset] = "類別"
      worksheet7Values[0][colOffset + 1] = "鄉鎮縣市"
      worksheet7Values[0][colOffset + 2] = "人數"

      const tempCountByGender = countByRegion(filteredCases)
      tempCountByGender.forEach((rowData, rowIndex) => {
        const row = rowIndex + 1;
        worksheet7Values[row] = worksheet7Values[row] || [];
        worksheet7Values[row][colOffset] = `${row === 1 ? (group.path === '' ? '整體' : group.shortName) : ''}`;
        worksheet7Values[row][colOffset + 1] = rowData.region;
        worksheet7Values[row][colOffset + 2] = rowData.count || 0;
      })
    })
    const worksheet7 = XLSX.utils.aoa_to_sheet(worksheet7Values);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet1, "總覽");
    XLSX.utils.book_append_sheet(workbook, worksheet2, "滿意度分析");
    XLSX.utils.book_append_sheet(workbook, worksheet3, "服務天數平均統計");
    XLSX.utils.book_append_sheet(workbook, worksheet4, "案件組別統計");
    XLSX.utils.book_append_sheet(workbook, worksheet5, "個案性別統計");
    XLSX.utils.book_append_sheet(workbook, worksheet6, "年齡區間統計");
    XLSX.utils.book_append_sheet(workbook, worksheet7, "個案鄉鎮市統計");

    saveWorkbook({ workbook, fileName: '貼心到宅數據報表'})
  }

  return { exportCasesDetail, exportCaseStatistics }
}
